import React, { useCallback, useEffect, useMemo, useState } from "react";
import { applyClientSession } from "../controllers/authController";
import {
  getSuperAdmin,
  getSwitchableClients,
  switchClient,
} from "../controllers/superAdminController";
import "./SuperAdminClientSwitcher.css";

/**
 * Header control that lets a super admin view any client's dashboard.
 *
 * Renders nothing for an ordinary client session. Picking a client asks the
 * backend for a session token scoped to them and then reloads: roughly twenty
 * components read localStorage.username once at mount, so remounting the whole
 * tree is the only reliable way to repoint the dashboard.
 *
 * This is the only way into the client list; SuperAdminBar is just a mode
 * indicator and carries no controls.
 */
const initials = (client) => {
  const source = client.client_name || client.username || "?";
  const words = source.trim().split(/\s+/).slice(0, 2);
  return words.map((w) => w[0]).join("").toUpperCase();
};

const SuperAdminClientSwitcher = () => {
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [switchingTo, setSwitchingTo] = useState(null);

  const superAdmin = getSuperAdmin();
  const activeUsername = localStorage.getItem("username") || "";

  const loadClients = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setClients(await getSwitchableClients());
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          e?.parsedError?.message ||
          "Could not load the client list.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on first open so an ordinary page load costs nothing.
  useEffect(() => {
    if (open && clients.length === 0 && !loading && !error) {
      loadClients();
    }
  }, [open, clients.length, loading, error, loadClients]);

  useEffect(() => {
    if (!open) return undefined;
    const onEscape = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [open]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return clients;
    return clients.filter(
      (c) =>
        c.username?.toLowerCase().includes(needle) ||
        c.client_name?.toLowerCase().includes(needle) ||
        c.client_id?.toLowerCase().includes(needle),
    );
  }, [clients, query]);

  const handleSelect = async (username) => {
    if (username === activeUsername) {
      setOpen(false);
      return;
    }
    setSwitchingTo(username);
    setError("");
    try {
      const data = await switchClient(username);
      applyClientSession(data.token, data.client, data.super_admin);
      window.location.reload();
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          e?.parsedError?.message ||
          "Could not switch to " + username + ".",
      );
      setSwitchingTo(null);
    }
  };

  if (!superAdmin?.username) return null;

  const activeClient = clients.find((c) => c.username === activeUsername);
  const activeLabel = activeClient?.client_name || activeUsername || "Select client";

  return (
    <div className="sa-switch">
      <button
        className={"sa-switch-trigger" + (open ? " sa-switch-trigger-open" : "")}
        onClick={() => setOpen((v) => !v)}
        title={"Super admin " + superAdmin.username + " — viewing " + activeUsername}
      >
        <svg
          className="sa-switch-trigger-shield"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
        <span className="sa-switch-trigger-text">
          <span className="sa-switch-trigger-kicker">Super Admin</span>
          <span className="sa-switch-trigger-client">{activeLabel}</span>
        </span>
        <span className="sa-switch-caret" aria-hidden="true">
          &#9660;
        </span>
      </button>

      {open && (
        <>
          <div className="sa-switch-backdrop" onClick={() => setOpen(false)} />
          <div className="sa-switch-panel">
            <div className="sa-switch-panel-head">
              <div className="sa-switch-panel-title">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
                View any client dashboard
              </div>
              <div className="sa-switch-panel-sub">
                signed in as <strong>{superAdmin.username}</strong>
                {clients.length > 0 && (
                  <span className="sa-switch-count">{clients.length} clients</span>
                )}
              </div>
            </div>

            <div className="sa-switch-search-wrap">
              <svg
                className="sa-switch-search-icon"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                autoFocus
                className="sa-switch-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search client, username or id..."
              />
            </div>

            {error && <div className="sa-switch-error">{error}</div>}

            <div className="sa-switch-list" role="listbox" aria-label="Client dashboards">
              {loading && <div className="sa-switch-muted">Loading clients...</div>}

              {!loading && filtered.length === 0 && (
                <div className="sa-switch-muted">
                  {clients.length === 0 ? "No clients found." : "No match."}
                </div>
              )}

              {!loading &&
                filtered.map((client) => {
                  const isActive = client.username === activeUsername;
                  const isSwitching = switchingTo === client.username;
                  const select = () => !switchingTo && handleSelect(client.username);
                  const className =
                    "sa-switch-row" +
                    (isActive ? " sa-switch-row-active" : "") +
                    (switchingTo && !isSwitching ? " sa-switch-row-dimmed" : "") +
                    (switchingTo ? " sa-switch-row-busy" : "");
                  return (
                    <div
                      key={client.username}
                      className={className}
                      role="option"
                      aria-selected={isActive}
                      tabIndex={0}
                      onClick={select}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          select();
                        }
                      }}
                    >
                      <span
                        className={
                          "sa-switch-avatar" +
                          (isActive ? " sa-switch-avatar-active" : "")
                        }
                        aria-hidden="true"
                      >
                        {initials(client)}
                      </span>
                      <span className="sa-switch-row-text">
                        <span className="sa-switch-row-name">
                          {client.client_name || client.username}
                          {!client.is_root_user && (
                            <span className="sa-switch-tag">sub-user</span>
                          )}
                          {client.tier === "premium" && (
                            <span className="sa-switch-tag sa-switch-tag-premium">
                              premium
                            </span>
                          )}
                        </span>
                        {/* One text node so "demo · mode" stays a single label. */}
                        <span className="sa-switch-row-meta">
                          {client.client_id
                            ? client.username + " · " + client.client_id
                            : client.username}
                        </span>
                      </span>
                      <span className="sa-switch-row-right">
                        {/* A switched-off dashboard is still openable here. */}
                        {client.effective_access === false && (
                          <span
                            className="sa-switch-tag sa-switch-tag-blocked"
                            title="Dashboard access is disabled for this client"
                          >
                            blocked
                          </span>
                        )}
                        {isSwitching && (
                          <span className="sa-switch-spinner" aria-label="Switching" />
                        )}
                        {isActive && !isSwitching && (
                          <span className="sa-switch-current">viewing</span>
                        )}
                      </span>
                    </div>
                  );
                })}
            </div>

            <div className="sa-switch-panel-foot">
              Actions you take here apply to the selected client's account.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SuperAdminClientSwitcher;
